import { useState } from 'react'
import { GoogleOutlined, LockOutlined, MailOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  theme,
  Typography,
} from 'antd'
import ForgotPasswordModal from './ForgotPasswordModal'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { errorResponse } from '../../lib/errorResponse'
import { hasInvitationQueryParameter } from '../../lib/hasInvitationQueryParameter'
import { getMultiFactorResolver } from 'firebase/auth'
import { useAuth } from 'reactfire'
import { Verify2faAuthenticationModal } from './Verify2faAuthenticationModal'

export default function LoginForm({
  emailLogin,
  googleSignup,
  sendResetPassword,
}) {
  const [emailLoading, setEmailLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [
    openVerify2faAuthenticationModal,
    setOpenVerify2faAuthenticationModal,
  ] = useState(false)

  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()
  const { token } = theme.useToken()

  const query = location.search
  const auth = useAuth()
  const { t } = useTranslation()

  const [resolver, setResolver] = useState(null)

  async function handle2faAuthentication(error) {
    const resolver = await getMultiFactorResolver(auth, error)
    setResolver(resolver)
    setOpenVerify2faAuthenticationModal(true)
  }

  async function handleEmail(values) {
    setEmailLoading(true)
    try {
      await emailLogin(values.email, values.password)
    } catch (error) {
      switch (error.code) {
        case 'auth/invalid-email':
          message.error(t('frontend:auth.emailInvalid'))
          break
        case 'auth/user-disabled':
          message.error(t('frontend:auth.userDisabledThis'))
          break
        case 'auth/user-not-found':
          message.error(t('frontend:auth.noUserWithEmail'))
          break
        case 'auth/wrong-password':
          message.error(t('frontend:auth.wrongPassword'))
          break
        // In case enable_improved_email_privacy is turned on this will be
        // the only response
        case 'auth/invalid-login-credentials':
        case 'auth/invalid-credential':
          message.error(t('frontend:auth.emailOrPasswordWrong'))
          break
        case 'auth/multi-factor-auth-required':
          await handle2faAuthentication(error)
          break
        default:
          /* eslint-disable-next-line no-console */
          console.error('Email login error', {
            code: error?.code,
            message: error?.message,
            error,
          })
          errorResponse(
            error,
            t('frontend:auth.loginFailed', {
              code: error?.code || t('frontend:common.unknown'),
            })
          )
          break
      }
    } finally {
      setEmailLoading(false)
    }
  }

  async function handleGoogle() {
    setGoogleLoading(true)
    try {
      await googleSignup()
    } catch (error) {
      switch (error.code) {
        case 'auth/account-exists-with-different-credential':
          message.error(t('frontend:auth.userAlreadyExists'))
          break
        case 'auth/unauthorized-domain':
          message.error(t('frontend:auth.domainNotAuthorized'))
          break
        case 'auth/auth-domain-config-required':
          message.error(t('frontend:auth.authDomainConfigMissing'))
          break
        // case 'auth/auth-domain-config-required':
        //   message.error('Auth Domain Config fehlt.')
        //   break
        // case 'auth/cancelled-popup-request':
        //   message.error('Popup Request abgebrochen.')
        //   break
        case 'auth/operation-not-allowed':
          message.error(t('frontend:auth.googleSigninDisabled'))
          break
        // case 'auth/operation-not-supported-in-this-environment':
        //   message.error('Operation nicht unterstützt.')
        //   break
        case 'auth/popup-blocked':
          message.error(t('frontend:auth.popupBlocked'))
          break
        // case 'auth/popup-closed-by-user':
        //   message.error('Popup geschlossen.')
        //   break
        case 'auth/web-storage-unsupported':
          message.error(t('frontend:auth.webStorageBlocked'))
          break
        case 'auth/multi-factor-auth-required':
          await handle2faAuthentication(error)
          break
        default:
          errorResponse(error)
          break
      }
    } finally {
      setGoogleLoading(false)
    }
  }

  const isInvitation = hasInvitationQueryParameter(searchParams)
  const isTaxAdvisorInvitation = searchParams.has('tax_advisor_invitation')
  const isDemoInvitation = searchParams.has('demo')

  let showEmail = true,
    showGoogle = true

  if (searchParams.has('provider_id')) {
    const providerId = searchParams.get('provider_id')
    // Explicitly check for the providers, so we show all
    // options if the provider is not handled here
    switch (providerId) {
      case 'password':
        showGoogle = false
        break
      case 'google.com':
        showEmail = false
        break
    }
  }

  const prefillEmail = searchParams.get('email') ?? ''

  return (
    <>
      {resolver && (
        <Verify2faAuthenticationModal
          open={openVerify2faAuthenticationModal}
          onCancel={() => setOpenVerify2faAuthenticationModal(false)}
          resolver={resolver}
          successAction={async () => {
            setOpenVerify2faAuthenticationModal(false)
          }}
        />
      )}
      <Card
        bordered={false}
        styles={{ body: { paddingBottom: '32px', paddingTop: '32px' } }}
      >
        <Row gutter={[0, 5]}>
          {isInvitation && (
            <Col span={24}>
              <>
                <Typography.Title level={3}>
                  {t(
                    isTaxAdvisorInvitation
                      ? 'frontend:auth.invitationTitleFormal'
                      : 'frontend:auth.invitationTitleInformal',
                    {
                      kind: isDemoInvitation
                        ? t('frontend:auth.demo')
                        : t('frontend:auth.invitation'),
                    }
                  )}
                </Typography.Title>
                <Typography.Paragraph>
                  <Trans
                    i18nKey={
                      isTaxAdvisorInvitation
                        ? 'frontend:auth.invitationLeadFormal'
                        : 'frontend:auth.invitationLeadInformal'
                    }
                    values={{ email: prefillEmail }}
                    components={{ strong: <Typography.Text strong /> }}
                  />
                </Typography.Paragraph>
              </>
              <Divider
                style={{
                  color: token.colorTextTertiary,
                  fontWeight: '500',
                  fontSize: '14px',
                }}
              ></Divider>
            </Col>
          )}
          {showGoogle && (
            <Col span={24}>
              <Button
                loading={googleLoading}
                icon={<GoogleOutlined />}
                type="primary"
                danger
                block
                onClick={handleGoogle}
              >
                {t('frontend:auth.signInWithGoogle')}
              </Button>
            </Col>
          )}
          {showGoogle && showEmail && (
            <Col span={24}>
              <Divider
                style={{
                  color: token.colorTextTertiary,
                  fontWeight: '500',
                  fontSize: '14px',
                }}
              >
                {t('frontend:auth.or')}
              </Divider>
            </Col>
          )}
          {showEmail && (
            <Col span={24}>
              <Form
                form={form}
                onFinish={handleEmail}
                className="login-form"
                initialValues={{ email: prefillEmail }}
              >
                <Form.Item
                  style={{ marginBottom: token.marginXS }}
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t('frontend:auth.emailRequired'),
                    },
                  ]}
                >
                  <Input
                    prefix={
                      <MailOutlined
                        style={{ color: token.colorTextQuaternary }}
                      />
                    }
                    placeholder="Email"
                    type="email"
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t('frontend:auth.passwordRequired'),
                    },
                  ]}
                >
                  <Input
                    prefix={
                      <LockOutlined
                        style={{ color: token.colorTextQuaternary }}
                      />
                    }
                    type="password"
                    placeholder={t('frontend:auth.password')}
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: '10px' }}>
                  <Button
                    loading={emailLoading}
                    block
                    type="primary"
                    htmlType="submit"
                    className="login-form-button"
                    id="login-form-button"
                  >
                    {t('frontend:auth.login')}
                  </Button>
                </Form.Item>
              </Form>
              <ForgotPasswordModal
                sendResetPassword={sendResetPassword}
                prefillEmail={prefillEmail}
              />
            </Col>
          )}
        </Row>
      </Card>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginTop: '15px' }}
      >
        {t('frontend:auth.notYetWithUs')}{' '}
        <Link to={`/signup${query}`}>{t('frontend:auth.createAccount')}</Link>
      </Typography.Text>
    </>
  )
}
